/**
 * Persistence Queue - Debounced, deduplicated sync queue.
 *
 * DEBOUNCE POLICY:
 *   - Standard: 5 second debounce window (per project)
 *   - Immediate flush triggers: runtime shutdown, deploy action,
 *     manual save request, tab close (visibilitychange:hidden), window blur
 *
 * DEDUPLICATION:
 *   - Only the LATEST entry per projectId is kept in the queue.
 *   - Intermediate saves during the debounce window are deduplicated by projectId.
 *   - The entry with the highest sequenceNum is retained.
 *
 * This means: rapid saves during a 5s window result in ONE cloud write,
 * carrying the most recent snapshot. All intermediate snapshots are only
 * in the offline journal (not lost, just not individually cloud-synced).
 */

import { logger } from "@/system/logger";
import type { ProjectContentPayload } from "./storageAdapter";

const DEBOUNCE_MS = 5_000;

export interface QueueEntry {
  payload:     ProjectContentPayload;
  enqueuedAt:  number;
}

type FlushCallback = (entries: QueueEntry[]) => Promise<void>;

export class PersistenceQueue {
  private queue    = new Map<string, QueueEntry>(); // keyed by projectId
  private timers   = new Map<string, ReturnType<typeof setTimeout>>();
  private onFlush: FlushCallback;
  private paused   = false;

  constructor(onFlush: FlushCallback) {
    this.onFlush = onFlush;
  }

  enqueue(payload: ProjectContentPayload): void {
    const existing = this.queue.get(payload.projectId);

    // Deduplicate: keep the entry with the highest sequenceNum
    if (existing && existing.payload.sequenceNum >= payload.sequenceNum) {
      logger.debug("PersistenceQueue deduplicated entry", {
        projectId:    payload.projectId,
        keptSeq:      existing.payload.sequenceNum,
        droppedSeq:   payload.sequenceNum,
      });
      return;
    }

    this.queue.set(payload.projectId, { payload, enqueuedAt: Date.now() });

    // Reset debounce timer for this project
    const existing_timer = this.timers.get(payload.projectId);
    if (existing_timer) clearTimeout(existing_timer);

    if (!this.paused) {
      const timer = setTimeout(() => this.flushProject(payload.projectId), DEBOUNCE_MS);
      this.timers.set(payload.projectId, timer);
    }
  }

  /** Flush all queued entries immediately (bypasses debounce). */
  async flushAll(): Promise<void> {
    // Cancel all pending debounce timers
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();

    const entries = [...this.queue.values()];
    this.queue.clear();

    if (entries.length === 0) return;

    logger.info("PersistenceQueue flushing all", { count: entries.length });
    await this.onFlush(entries);
  }

  /** Flush a single project's queued entry. */
  private async flushProject(projectId: string): Promise<void> {
    this.timers.delete(projectId);
    const entry = this.queue.get(projectId);
    if (!entry) return;
    this.queue.delete(projectId);
    await this.onFlush([entry]);
  }

  pause(): void {
    this.paused = true;
    // Cancel debounce timers - entries remain in queue
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
    logger.info("PersistenceQueue paused (offline)");
  }

  resume(): void {
    this.paused = false;
    // Reschedule debounce timers for all queued entries (immediate flush)
    for (const [projectId] of this.queue) {
      const timer = setTimeout(() => this.flushProject(projectId), 0);
      this.timers.set(projectId, timer);
    }
    logger.info("PersistenceQueue resumed (online)", { pending: this.queue.size });
  }

  get pendingCount(): number {
    return this.queue.size;
  }

  isQueued(projectId: string): boolean {
    return this.queue.has(projectId);
  }
}
